import type { FC } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import AkButton from '@atlaskit/button';
import { ProgressIndicator as AkProgressDots } from '@atlaskit/progress-indicator';
import { ModalFooter as AkModalFooter } from '@atlaskit/modal-dialog';

import type { OnboardingSlide } from './OnboardingModal';

export type OnboardingModalFooterProps = {
	selectedIndex: number;
	isLastSlide: boolean;
	slides: OnboardingSlide[];
	onSkip: () => void;
	onNext: () => void;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledFlexContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledFlexColumn = styled.div<{ align?: string }>({
	flex: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textAlign: (props) => (props.align ? props.align : 'inherit'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledProgressDots = styled.div({
	marginTop: token('space.150', '12px'),
});

export const OnboardingModalFooter: FC<OnboardingModalFooterProps> = ({
	selectedIndex,
	isLastSlide,
	onSkip,
	onNext,
	slides,
}) => {
	const slideTitles: JSX.Element[] = slides.map((slide) => slide.title);
	const numOfSlides = slides.length;
	const currentSlideUrl = slides[selectedIndex].learnMoreUrl;

	const renderPrimaryCTA = () =>
		currentSlideUrl ? (
			<AkButton appearance="primary" href={currentSlideUrl} target="_blank">
				<FormattedMessage
					id="premium-onboarding.button.learn.more"
					description="Button to learn more about the slide"
					defaultMessage="Learn more"
				/>
			</AkButton>
		) : (
			<AkButton onClick={onNext} appearance="primary">
				{isLastSlide ? (
					<FormattedMessage
						id="premium-onboarding.button.done"
						description="Done button to complete the onboarding slides"
						defaultMessage="Done"
					/>
				) : (
					<FormattedMessage
						id="premium-onboarding.button.next"
						description="Next button to navigate to the next slide"
						defaultMessage="Next"
					/>
				)}
			</AkButton>
		);

	const renderSecondaryCTA = () => (
		<AkButton appearance="subtle-link" onClick={onSkip}>
			<FormattedMessage
				id="premium-onboarding.button.dismiss"
				description="Button to dismiss the onboarding flow"
				defaultMessage="Dismiss"
			/>
		</AkButton>
	);

	const renderProgressIndicator = () => (
		<StyledProgressDots data-testid="progress-indicator">
			<AkProgressDots selectedIndex={selectedIndex} values={slideTitles} />
		</StyledProgressDots>
	);

	return (
		<AkModalFooter>
			<StyledFlexContainer>
				<StyledFlexColumn>{renderSecondaryCTA()}</StyledFlexColumn>
				<StyledFlexColumn>{numOfSlides < 2 ? null : renderProgressIndicator()}</StyledFlexColumn>
				<StyledFlexColumn align="right">{renderPrimaryCTA()}</StyledFlexColumn>
			</StyledFlexContainer>
		</AkModalFooter>
	);
};
